import axios from "axios";
import { useContext } from "react";
import AppContext from "../../../context/AppContext";
import React, { useState } from "react";
import Modal from "../../../components/customer/modal/Modal";
import ImageUploader from "../../../components/customer/imageUploader/ImageUploader";
import "../../../assets/css/customer/createBidding/CreateBidding.css";

const CreateBidding = ({ isOpen, onClose }) => {
  const { baseUrl, authToken } = useContext(AppContext);
  const [formData, setFormData] = useState({
    productName: "",
    condition: "",
    description: "",
    startingPrice: "",
    minBidIncrement: "500",
    startDate: "",
    location: "",
    phone: "",
  });

  const [productImages, setProductImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // 🧪 TESTING ONLY: endDate = startDate + 1 minute (normal: 24 hours)
  // ⚠️ Testing khatam hone ke baad wapis 24 * 60 * 60 * 1000 kar dena!
  // ✅ Fix: toISOString() UTC deta hai jo datetime-local field mein galat time dikhata tha.
  //    Ab local time components se hi string banayi ja rahi hai.
  const getEndDate = (startDate) => {
    if (!startDate) return "";
    const end = new Date(new Date(startDate).getTime() + 1 * 60 * 1000);

    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = end.getFullYear();
    const mm = pad(end.getMonth() + 1);
    const dd = pad(end.getDate());
    const hh = pad(end.getHours());
    const min = pad(end.getMinutes());

    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleImagesChange = async (files) => {
    try {
      const converted = await Promise.all(
        files.map(async (file) => ({
          file,
          preview: URL.createObjectURL(file),
          base64: await fileToBase64(file),
          name: file.name,
          size: file.size,
          type: file.type,
        }))
      );
      setProductImages(converted);
      if (errors.productImages) {
        setErrors((prev) => ({ ...prev, productImages: "" }));
      }
    } catch (err) {
      setErrors((prev) => ({ ...prev, productImages: "Failed to process images." }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.productName.trim()) newErrors.productName = "Product name is required";
    if (!formData.condition) newErrors.condition = "Condition is required";
    if (!formData.description.trim()) newErrors.description = "Description is required";
    else if (formData.description.trim().length < 20) newErrors.description = "Minimum 20 characters";
    if (!formData.startingPrice || formData.startingPrice <= 0) newErrors.startingPrice = "Valid starting price is required";
    if (!formData.minBidIncrement || formData.minBidIncrement <= 0) newErrors.minBidIncrement = "Valid minimum increment is required";
    if (!formData.startDate) newErrors.startDate = "Start date is required";
    if (!formData.location.trim()) newErrors.location = "Location is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!/^[0-9]{4}-[0-9]{7}$/.test(formData.phone)) newErrors.phone = "Format: 0312-3456789";
    if (productImages.length === 0) newErrors.productImages = "At least one image is required";
    return newErrors;
  };

  const resetForm = () => {
    productImages.forEach((img) => URL.revokeObjectURL(img.preview));
    setFormData({
      productName: "",
      condition: "",
      description: "",
      startingPrice: "",
      minBidIncrement: "500",
      startDate: "",
      location: "",
      phone: "",
    });
    setProductImages([]);
    setErrors({});
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setIsSubmitting(true);

    // endDate auto = startDate + 1 minute (testing)
    const endDate = getEndDate(formData.startDate);

    const formDataToSend = new FormData();
    formDataToSend.append('productName', formData.productName);
    formDataToSend.append('condition', formData.condition);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('startingPrice', formData.startingPrice);
    formDataToSend.append('minBidIncrement', formData.minBidIncrement);
    formDataToSend.append('location', formData.location);
    formDataToSend.append('startDate', formData.startDate);
    formDataToSend.append('endDate', endDate); // ← auto calculated
    formDataToSend.append('phone', formData.phone);
    productImages.forEach((img) => {
      formDataToSend.append('bidImages[]', img.file);
    });

    axios.post(
      `${baseUrl}/customer/bidding/postbidding`,
      formDataToSend,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    )
      .then((res) => {
        console.log(res.data);
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          resetForm();
          onClose();
        }, 2000);
      })
      .catch((err) => {
        console.log(err.response?.data || err.message);
        setErrors({ submit: err.response?.data?.message || 'Something went wrong. Please try again.' });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleClose = () => {
    if (!isSubmitting) { resetForm(); onClose(); }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Post New Item for Bidding" size="large">
      {showSuccess ? (
        <div className="createbid-success-container">
          <div className="createbid-success-icon">✓</div>
          <h3>Bidding Posted Successfully!</h3>
          <p>Your item is now live and ready for bidding.</p>
          <p className="createbid-success-note">You'll receive notifications when people place bids.</p>
        </div>
      ) : (
        <form className="createbid-form" onSubmit={handleSubmit}>

          {/* Images */}
          <div className="createbid-section">
            <h4 className="createbid-section-title">Product Images *</h4>
            <ImageUploader images={productImages} onChange={handleImagesChange} maxImages={5} />
            {errors.productImages && <span className="createbid-error-text">{errors.productImages}</span>}
            <span className="createbid-helper-text">Upload up to 5 images. First image will be the main display.</span>
          </div>

          {/* Basic Info */}
          <div className="createbid-section">
            <h4 className="createbid-section-title">Basic Information</h4>
            <div className="createbid-form-grid">

              <div className="createbid-form-group createbid-full-width">
                <label htmlFor="productName">Product Name *</label>
                <input type="text" id="productName" name="productName" value={formData.productName} onChange={handleChange} className={errors.productName ? "createbid-error" : ""} placeholder="e.g., Vintage Leather Sofa" />
                {errors.productName && <span className="createbid-error-text">{errors.productName}</span>}
              </div>

              <div className="createbid-form-group">
                <label htmlFor="condition">Condition *</label>
                <select id="condition" name="condition" value={formData.condition} onChange={handleChange} className={errors.condition ? "createbid-error" : ""}>
                  <option value="">Select condition</option>
                  <option value="Brand New">Brand New</option>
                  <option value="Used - Like New">Used - Like New</option>
                  <option value="Used - Good">Used - Good</option>
                  <option value="Used - Fair">Used - Fair</option>
                </select>
                {errors.condition && <span className="createbid-error-text">{errors.condition}</span>}
              </div>

              <div className="createbid-form-group">
                <label htmlFor="phone">Contact Number *</label>
                <input type="text" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={errors.phone ? "createbid-error" : ""} placeholder="e.g., 0312-3456789" />
                {errors.phone && <span className="createbid-error-text">{errors.phone}</span>}
              </div>

              <div className="createbid-form-group createbid-full-width">
                <label htmlFor="location">Location *</label>
                <input type="text" id="location" name="location" value={formData.location} onChange={handleChange} className={errors.location ? "createbid-error" : ""} placeholder="e.g., Gujranwala, Punjab" />
                {errors.location && <span className="createbid-error-text">{errors.location}</span>}
              </div>

              <div className="createbid-form-group createbid-full-width">
                <label htmlFor="description">Description *</label>
                <textarea id="description" name="description" value={formData.description} onChange={handleChange} className={errors.description ? "createbid-error" : ""} placeholder="Describe your item (minimum 20 characters)" rows={4} />
                {errors.description && <span className="createbid-error-text">{errors.description}</span>}
                <span className="createbid-helper-text">{formData.description.length}/500 characters</span>
              </div>

            </div>
          </div>

          {/* Pricing */}
          <div className="createbid-section">
            <h4 className="createbid-section-title">Pricing</h4>
            <div className="createbid-form-grid">
              <div className="createbid-form-group">
                <label htmlFor="startingPrice">Starting Price (PKR) *</label>
                <input type="number" id="startingPrice" name="startingPrice" value={formData.startingPrice} onChange={handleChange} placeholder="50000" min="0" />
                {errors.startingPrice && <span className="createbid-error-text">{errors.startingPrice}</span>}
              </div>
              <div className="createbid-form-group">
                <label htmlFor="minBidIncrement">Minimum Bid Increment (PKR) *</label>
                <input type="number" id="minBidIncrement" name="minBidIncrement" value={formData.minBidIncrement} onChange={handleChange} placeholder="500" min="1" />
                {errors.minBidIncrement && <span className="createbid-error-text">{errors.minBidIncrement}</span>}
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="createbid-section">
            <h4 className="createbid-section-title">Bidding Schedule</h4>
            <div className="createbid-form-grid">
              <div className="createbid-form-group">
                <label htmlFor="startDate">Start Date & Time *</label>
                <input
                  type="datetime-local"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  min={new Date().toISOString().slice(0, 16)}
                  className={errors.startDate ? "createbid-error" : ""}
                />
                {errors.startDate && <span className="createbid-error-text">{errors.startDate}</span>}
              </div>

              {/* End date auto show */}
              {formData.startDate && (
                <div className="createbid-form-group">
                  <label>End Date & Time <span style={{ fontSize: "0.75rem", color: "#888" }}>(auto — 1 min)</span></label>
                  <input
                    type="datetime-local"
                    value={getEndDate(formData.startDate)}
                    disabled
                    style={{ background: "#f5f5f5", color: "#888", cursor: "not-allowed" }}
                  />
                  <span className="createbid-helper-text" style={{ color: "#d67e43" }}>
                    ⏱ Bidding will end after 1 minute
                  </span>
                </div>
              )}
            </div>
          </div>

          {errors.submit && <p className="createbid-error-text" style={{ textAlign: "center" }}>{errors.submit}</p>}

          <div className="createbid-form-actions">
            <button type="button" className="createbid-cancel-btn" onClick={handleClose} disabled={isSubmitting}>Cancel</button>
            <button type="submit" className="createbid-submit-btn" disabled={isSubmitting}>
              {isSubmitting ? (
                <span style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                  <span className="createbid-spinner" />
                  Posting...
                </span>
              ) : "Post Bidding"}
            </button>
          </div>

        </form>
      )}
    </Modal>
  );
};

export default CreateBidding;